import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  createTheme,
  ThemeProvider,
  useTheme,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import TextFieldGroup from '../../inputs/TextFieldGroupNoLabel';
import EditDealershipSettings from '../../inputs/EditDealershipSettings';
import { lookupDepartmentByName } from '../../actions/lookups';
import {
  updateWorkFlow,
  deleteWorkFlow,
  deleteWorkFlowProperty,
  addWorkFlowProperty,
  addWorkFlow,
} from '../../actions/apiWorkFlows';
import { editWorkFlows, setCurrentReportTab } from '../../actions/apiSetInfo';
import _ from 'lodash';

const StepWorkFlow = () => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();
  const departments = useSelector((state) => state.settings_departments.original_departments || []);
  const work_flows = useSelector((state) => state.settings_departments.work_flows || []);
  const edit_work_flows = useSelector((state) => state.settings_site.edit_work_flows);
  const dealer_settings = useSelector((state) => state.settings_site.dealer_settings || []);
  const installation = useSelector((state) => state.settings_dealerships.installation || {});
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [newWorkFlowName, setNewWorkFlowName] = useState('');
  const [errors, setErrors] = useState({
    addWorkFlowError: false,
  });

  const [state, setState] = useState({
    abbreviation: '',
    goal: '',
    work_flow_exits: '',
  });

  // Sync state with selected workflow
  useEffect(() => {
    if (edit_work_flows) {
      const work_flow = _.find(work_flows, { id: parseInt(edit_work_flows) }) || {};
      setState({
        abbreviation: work_flow.abbreviation || '',
        goal: work_flow.goal || '',
        work_flow_exits: work_flow.work_flow_exits || '',
      });
    } else {
      setState({
        abbreviation: '',
        goal: '',
        work_flow_exits: '',
      });
    }
  }, [edit_work_flows, work_flows]);

  const onChange = (e) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onBlur = (e) => {
    const data = {
      id: edit_work_flows,
      abbreviation: e.target.value,
    };
    dispatch(updateWorkFlow(edit_work_flows, data, 'abbreviation'));
  };

  const onGoalBlur = (e) => {
    const data = {
      id: edit_work_flows,
      goal: e.target.value,
    };
    dispatch(updateWorkFlow(edit_work_flows, data, 'goal'));
  };

  const onWorkFlowExitsChange = (e) => {
    const selectedValues = Array.isArray(e.target.value) ? e.target.value : [];
    setState((prev) => ({ ...prev, work_flow_exits: selectedValues.join(',') }));
    const data = {
      id: edit_work_flows,
      work_flow_exits: selectedValues.join(','),
    };
    dispatch(updateWorkFlow(edit_work_flows, data, 'work_flow_exits'));
  };

  const dealerSettingValue = (name) => {
    let value = dealer_settings
      .filter((o) => o.key === name && o.dealership === installation.subdomain)
      .sort((a, b) => a.id - b.id);
    if (value.length === 0) {
      value = dealer_settings
        .filter((o) => o.key === name && (o.dealership === '' || o.dealership === null || o.dealership === undefined))
        .sort((a, b) => a.id - b.id);
    }
    return value.length > 0 && value[0]?.value !== undefined ? value[0].value : '';
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setNewWorkFlowName('');
    setErrors((prev) => ({ ...prev, addWorkFlowError: false }));
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewWorkFlowName('');
    setErrors((prev) => ({ ...prev, addWorkFlowError: false }));
  };

  const workFlowExists = (name) => {
    return work_flows.some((w) => (w.name || '').toLowerCase() === (name || '').toLowerCase());
  };

  const validateWorkFlow = (name) => {
    const result = {
      isValid: true,
      message: '',
    };
    if (!name || !name.trim()) {
      result.isValid = false;
      result.message = 'Workflow name must not be empty';
    }
    if (workFlowExists(name)) {
      result.isValid = false;
      result.message = `Workflow with name "${name}" already exists`;
    }
    return result;
  };

  const handleAddWorkFlow = () => {
    const { isValid, message } = validateWorkFlow(newWorkFlowName);
    if (!isValid) {
      setErrors((prev) => ({ ...prev, addWorkFlowError: true, errorMessage: message }));
      return;
    }
    dispatch(addWorkFlow({ name: newWorkFlowName }, 'addWorkFlow')).then((resp) => {
      if (resp && resp.errors) {
        setErrors((prev) => ({ ...prev, addWorkFlowError: true, errorMessage: 'Error adding workflow' }));
      } else {
        handleCloseAddModal();
      }
    });
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteWorkFlow(edit_work_flows)).then(() => {
      handleCloseDeleteModal();
    });
  };

  const toggleWorkFlowProperty = (departmentId) => {
    const workFlow = edit_work_flows && work_flows.length > 0 ? _.find(work_flows, { id: parseInt(edit_work_flows) }) || {} : {};
    const workFlowProperty = workFlow.work_flow_properties?.find((prop) => prop.department_id === departmentId);
    
    if (workFlowProperty) {
      dispatch(deleteWorkFlowProperty(workFlowProperty.id, 'deleteWorkFlowProperty'));
    } else {
      const data = {
        work_flow_id: parseInt(edit_work_flows),
        department_id: departmentId,
        order: 99,
      };
      dispatch(addWorkFlowProperty(data, 'AddWorkFlowProperty'));
    }
  };

  const workFlow = edit_work_flows && work_flows.length > 0 ? _.find(work_flows, { id: parseInt(edit_work_flows) }) || {} : {};
  const workFlowId = workFlow.id;
  const workflowName = (workFlow.name || 'Workflow').toUpperCase();
  const delete_disabled = workFlowId && (workFlow.name === 'used' || workFlow.name === 'new' || workFlow.name === 'wholesale');

  // Debug log to diagnose disabled button issue (uncomment to use)
  // console.log('Debug: workFlowId:', workFlowId, 'workFlow.name:', workFlow.name, 'delete_disabled:', delete_disabled, 'edit_work_flows:', edit_work_flows, 'work_flows:', work_flows);

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: { main: '#007bff' },
        },
        components: {
          MuiTableHead: {
            styleOverrides: {
              root: {
                border: 'none',
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  const uniqueDealerships = useMemo(
    () =>
      _.uniq(
        departments
          .filter((dept) => dept.dealerships && typeof dept.dealerships === 'string')
          .flatMap((dept) => dept.dealerships.split(',').map((d) => d.trim()))
      ).sort(),
    [departments],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Department Name',
        size: 150,
        filterVariant: 'text',
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.name || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'department_type',
        header: 'Type',
        size: 100,
        filterVariant: 'select',
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="data step">Data Step</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        ),
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.department_type || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'dealerships',
        header: 'Dealerships',
        size: 200,
        filterVariant: 'select',
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {uniqueDealerships.map((dealership) => (
                <MenuItem key={dealership} value={dealership}>
                  {dealership}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const dealershipArray = row.original.dealerships ? row.original.dealerships.split(',').map((d) => d.trim()) : [];
          return dealershipArray.includes(filterValue);
        },
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.dealerships || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'in_workflow',
        header: workFlowId ? `In ${workflowName} Workflow` : 'In Workflow',
        size: 100,
        filterVariant: 'checkbox',
        Cell: ({ row }) => {
          const workFlow = edit_work_flows && work_flows.length > 0 ? _.find(work_flows, { id: parseInt(edit_work_flows) }) || {} : {};
          const isInWorkflow = workFlow.work_flow_properties?.some((prop) => prop.department_id === row.original.id) || false;
          return (
            <Checkbox
              checked={isInWorkflow}
              onChange={() => toggleWorkFlowProperty(row.original.id)}
              disabled={!edit_work_flows}
            />
          );
        },
      },
    ],
    [edit_work_flows, work_flows, workFlowId, workflowName, uniqueDealerships],
  );

  const tableData = useMemo(
    () =>
      _.sortBy(
        departments.map((dept) => ({
          id: dept.id,
          name: dept.name,
          department_type: dept.department_type || 'other',
          dealerships: dept.dealerships || '',
          order: dept.order != null ? dept.order : Infinity, // Fallback to Infinity for undefined order
        })),
        'order'
      ),
    [departments],
  );

  let abbreviation = '';
  let goal = '';
  let work_flow_exits = '';

  if (workFlowId) {
    abbreviation = (
      <div className="col-sm-2 bg-light text-dark text-center">
        <label className="text-center"><strong>Abbreviation</strong></label>
        <TextFieldGroup
          field="abbreviation"
          label="Enter Abbreviation"
          onChange={onChange}
          onBlur={onBlur}
          value={state.abbreviation || workFlow.abbreviation || ''}
          type="text"
        />
      </div>
    );
    goal = (
      <div className="col-sm-2 bg-light text-dark text-center">
        <label className="text-center"><strong>Goal</strong></label>
        <TextFieldGroup
          field="goal"
          label="Enter Goal"
          onChange={onChange}
          onBlur={onGoalBlur}
          value={state.goal || workFlow.goal || ''}
          type="text"
        />
      </div>
    );
    work_flow_exits = (
      <div className="col-sm-2 bg-light text-dark text-center">
        <label className="text-center"><strong>{workflowName} Workflow Exits</strong></label>
        <FormControl fullWidth sx={{ minWidth: 120 }}>
          <InputLabel>{workflowName} Workflow Exits</InputLabel>
          <Select
            multiple
            value={state.work_flow_exits.split(',').filter(Boolean).map(Number)}
            onChange={onWorkFlowExitsChange}
            renderValue={(selected) => {
              const selectedNames = selected.map((id) => {
                const dept = departments.find((d) => d.id === id);
                return dept ? dept.name : id;
              });
              return selectedNames.join(', ');
            }}
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                <Checkbox checked={state.work_flow_exits.split(',').map(Number).includes(dept.id)} />
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <style>
        {`
          .setscroll {
            min-height: 400px;
            overflow-y: auto;
            width: 100%;
          }
        `}
      </style>
      <Dialog open={addModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add Workflow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workflow Name"
            type="text"
            fullWidth
            value={newWorkFlowName}
            onChange={(e) => setNewWorkFlowName(e.target.value)}
            error={errors.addWorkFlowError}
            helperText={errors.addWorkFlowError ? errors.errorMessage : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="error">
            Cancel
          </Button>
          <Button onClick={handleAddWorkFlow} color="success">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Delete {workflowName} Workflow</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete the workflow "{workflowName}"?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="error">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="warning">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <div className="col-md-12">
        <div className="row">
          <div className="card col-md-12">
            <div className="card-body">
              {errors.addWorkFlowError && (
                <div className="alert alert-danger">Error adding workflow: {errors.errorMessage}</div>
              )}
              <div className="d-flex flex-column">
                <div className="p-2 mb-3 row">
                  <div className="col-sm-2 bg-light text-dark text-center">
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <InputLabel>Manage Workflow</InputLabel>
                      <Select
                        value={edit_work_flows || ''}
                        onChange={(e) => dispatch(editWorkFlows(e.target.value))}
                        label="Manage Workflow"
                      >
                        <MenuItem value="">None Selected</MenuItem>
                        {work_flows.map((wf) => (
                          <MenuItem key={wf.id} value={wf.id}>
                            {(wf.name || 'Unnamed Workflow').toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="col-sm-2 bg-light text-dark text-center">
                    <Button
                      variant="contained"
                      color="success"
                      className="btn btn-success"
                      onClick={handleOpenAddModal}
                      style={{ marginTop: '16px' }}
                    >
                      Add Workflow
                    </Button>
                  </div>
                  {workFlowId && (
                    <>
                      <div className="col-sm-2 bg-light text-dark text-center">
                        <Button
                          variant="contained"
                          color="error"
                          className="btn btn-danger"
                          onClick={handleOpenDeleteModal}
                          disabled={delete_disabled}
                          style={{ marginTop: '16px' }}
                        >
                          Delete {workflowName} Workflow
                        </Button>
                      </div>
                      {abbreviation}
                      {goal}
                    </>
                  )}
                </div>
                {workFlowId && (
                  <div className="p-2 mb-3 row">
                    {work_flow_exits}
                  </div>
                )}
                <div className="p-2 list-group setscroll">
                  <ThemeProvider theme={tableTheme}>
                    <MaterialReactTable
                      columns={columns}
                      data={tableData}
                      enableColumnFilters
                      initialState={{
                        showColumnFilters: true,
                        pagination: { pageSize: 1000 },
                      }}
                      muiTablePaperProps={{
                        sx: { width: '100%' },
                      }}
                    />
                  </ThemeProvider>
                </div>
              </div>
            </div>
            <div className="card-footer" />
          </div>
        </div>
      </div>
      <div className="card space-top-0">
        <h3 className="text-center pad-top-1">Select Default Workflows for Vehicle Types (used in determining actual recon time)</h3>
        <div className="card bg-light space-bottom-1 space-top-1 mx-auto w-75 p-3">
          <div className="card-text pad-top-2">
            <div className="row">
              <div className="col-md-6">
                <EditDealershipSettings
                  type="select-recon-workflow"
                  name="recon_new_work_flow"
                  label="recon_new_work_flow"
                  value={dealerSettingValue('recon_new_work_flow')}
                />
              </div>
              <div className="col-md-6">
                <EditDealershipSettings
                  type="select-recon-workflow"
                  name="recon_used_work_flow"
                  label="recon_used_work_flow"
                  value={dealerSettingValue('recon_used_work_flow')}
                />
              </div>
              <div className="col-md-6">
                <EditDealershipSettings
                  type="select-recon-workflow"
                  name="recon_wholesale_work_flow"
                  label="recon_wholesale_work_flow"
                  value={dealerSettingValue('recon_wholesale_work_flow')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepWorkFlow;